/*
  Sensimilla — carrello rapido v2
  - Store unico: legge il carrello, calcola livello omaggi (stesso calcolo dello sconto Shopify
    "Omaggio Sensimilla"), tiene un solo omaggio valido, toglie quelli non piu' validi, gestisce messaggi.
  - <sens-cart-bar>: barra omaggi scorrevole (snippets/sens-cart-bar.liquid), con la spedizione gratuita
    come traguardo a parte tra i livelli omaggio.
  - <sens-gift-picker>: scelta dell'omaggio (snippets/sens-gift-picker.liquid). Sostituisce il riquadro
    dell'app omaggi, che resta attiva solo come garanzia delle regole (lo sconto lo applica Shopify).

  Regole verificate (2026-10-12):
  - l'omaggio va a 0 EUR solo se aggiunto con le proprieta' _free_product/_rule_id/_kite_promo_name,
    se il livello e' raggiunto e se la variante appartiene a quel livello;
  - soglia: si usa il calcolo dell'app omaggi (prezzi DOPO i codici sconto sui prodotti, senza omaggi),
    che e' quello che il cliente vede sul live. Lo sconto Shopify sul server e' piu' permissivo
    (subtotale prima degli sconti), quindi tutto cio' che l'app concede e' valido anche al checkout.
*/
(() => {
  if (window.SensCart) return;

  // ---------------------------------------------------------------------------
  // Costanti
  // ---------------------------------------------------------------------------
  const CAMPAIGN_TITLE = 'Omaggio Sensimilla';
  const CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const DRAWER_OPEN_CLASS = 'open-modal-cart-drawer'; // messa dal tema su <html> col carrello rapido aperto
  const KEY_GIFT_TIER = 'sens:gift-tier'; // livello a cui il cliente ha scelto l'omaggio
  const RESET_MS = 3000; // barra: ritorno automatico al livello dopo un'interazione
  const WIN_POP_MS = 5000; // mobile: durata del fumetto di vincita
  const INFO_POP_MS = 6000; // durata del fumetto informativo
  const TOAST_MS = 6500; // desktop: durata del messaggio di vincita
  const REMIND_MS = 10000; // promemoria "scegli il tuo omaggio" a carrello aperto
  const EDGE = 40; // px: distanza dal bordo del primo traguardo del blocco visibile
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia('(max-width: 767.98px)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const ss = {
    get(k) {
      try {
        return sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set(k, v) {
      try {
        sessionStorage.setItem(k, v);
      } catch (e) {}
    },
  };

  // ---------------------------------------------------------------------------
  // Rete
  // fetch "pulito" (da un iframe vuoto): le nostre richieste non passano dagli intercettori
  // di tema e app omaggi, cosi' non generano reazioni a catena.
  // ---------------------------------------------------------------------------
  const pristineFetch = (() => {
    try {
      const f = document.createElement('iframe');
      f.style.display = 'none';
      f.setAttribute('aria-hidden', 'true');
      f.tabIndex = -1;
      (document.body || document.documentElement).appendChild(f);
      return f.contentWindow.fetch.bind(f.contentWindow);
    } catch (e) {
      return window.fetch.bind(window);
    }
  })();
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const getCart = () =>
    pristineFetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const postJSON = (path, body) =>
    pristineFetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) =>
      r.json().then((j) => {
        if (!r.ok) throw j;
        return j;
      })
    );

  // Carrello rapido del tema: le sezioni da ridisegnare e renderContents (come fa il tema stesso)
  const drawerEl = () => document.querySelector('cart-drawer');
  const drawerSectionIds = () => {
    const d = drawerEl();
    const id = d && (d.getAttribute('data-section-id') || (d.dataset && d.dataset.sectionId));
    return id ? `${id},cart-icon-bubble` : null;
  };
  const renderDrawer = (sections) => {
    const d = drawerEl();
    if (!d || !sections || typeof d.renderContents !== 'function') return;
    try {
      d.renderContents({ sections });
    } catch (e) {}
  };

  // ---------------------------------------------------------------------------
  // Carrello: righe omaggio e calcolo del livello
  // ---------------------------------------------------------------------------
  const isGiftLine = (i) => !!(i && i.properties && i.properties._free_product === 'true');
  // Stesso calcolo dell'app omaggi (rules._calcCartTotal): somma dei prezzi scontati delle righe
  // (dopo i codici sconto sui prodotti), senza le righe omaggio. E' la regola che vede il cliente sul live.
  const lineValue = (i) => (Number.isFinite(i.discounted_price) ? i.discounted_price * i.quantity : i.final_line_price);
  const giftMetric = (cart) => cart.items.reduce((s, i) => (isGiftLine(i) ? s : s + lineValue(i)), 0);
  const cleanGiftTitle = (t) => String(t || '').replace(/\s*[-–|]?\s*omaggio$/i, '').trim();

  // ---------------------------------------------------------------------------
  // Store
  // ---------------------------------------------------------------------------
  const store = {
    ready: false,
    cfg: null, // configurazione barra (snippet)
    tiers: [], // [{name, min, cents, hash, oil, ext, segmentId, gifts:[{variantId, title, image}]}]
    fsCents: 0,
    fsReached: false,
    rate: 1,
    cart: null,
    metric: 0,
    tier: -1,
    gift: null, // omaggio valido nel carrello: {key, variantId, title, tier}
    busy: false, // aggiunta/cambio omaggio in corso
    pendingVariant: null, // omaggio appena scelto (mostrato subito, prima della risposta)
    notice: null, // 'up' | 'down' | 'change' | null
    noticeAgain: false, // "sceglilo di nuovo" (l'omaggio precedente e' stato tolto)
    listOpen: false,
    error: null,
    lastCelebration: null, // {from, to, at}
    _subs: new Set(),
    _seq: 0,
    _applied: 0,
  };
  const emit = (type, detail) => store._subs.forEach((fn) => fn(type, detail));
  const subscribe = (fn) => {
    store._subs.add(fn);
    return () => store._subs.delete(fn);
  };

  const tierFor = (metric) => {
    if (metric <= 0) return -1;
    let t = -1;
    store.tiers.forEach((tier, i) => {
      if (metric >= tier.cents) t = i;
    });
    return t;
  };

  // Config dei livelli: soglie e testi dallo snippet; soglie, id e omaggi dall'app (se disponibile)
  const appCampaign = () => {
    const list = window.gfg && window.gfg.settings && window.gfg.settings.freeGiftsV2;
    if (!Array.isArray(list)) return null;
    return list.find((c) => c && c.title === CAMPAIGN_TITLE && c.isEnabled) || null;
  };
  const buildTiers = () => {
    const cfg = store.cfg;
    const camp = appCampaign();
    const segments =
      camp && camp.segmentsData && Array.isArray(camp.segmentsData.segmentsList)
        ? camp.segmentsData.segmentsList
            .map((s) => {
              const rule = ((s.buyXRulesData && s.buyXRulesData.rulesList) || []).find((r) => r.ruleType === 'cartSubtotal');
              const gifts = [];
              ((s.getYRulesData && s.getYRulesData.rulesList) || []).forEach((r) =>
                ((r.ruleValue && r.ruleValue.value) || []).forEach((p) =>
                  (p.variants || []).forEach((v) => {
                    const vt = v.variantTitle && v.variantTitle !== 'Default Title' ? ` · ${v.variantTitle}` : '';
                    gifts.push({
                      variantId: Number(v.variantId),
                      title: cleanGiftTitle(p.title) + vt,
                      image: p.image || (p.images && p.images[0]) || '',
                    });
                  })
                )
              );
              return { segmentId: s.segmentId, min: rule ? Number(rule.ruleValue.qualifierValue) : null, gifts };
            })
            .filter((s) => s.min !== null)
            .sort((a, b) => a.min - b.min)
        : null;
    store.tiers = cfg.tiers.map((t, i) => {
      const seg = segments && segments.length === cfg.tiers.length ? segments[i] : null;
      const min = seg ? seg.min : t.min;
      return Object.assign({}, t, {
        min,
        cents: Math.round(Math.max(min, 0.01) * 100 * store.rate),
        segmentId: seg ? seg.segmentId : null,
        gifts: seg ? seg.gifts : [],
      });
    });
    store.fsCents = Math.round((cfg.free_shipping || 0) * 100 * store.rate);
  };

  // Applica un nuovo stato del carrello (risposta di una richiesta o lettura di /cart.js)
  const applyCart = (cart, seq = ++store._seq, opts = {}) => {
    if (!cart || !Array.isArray(cart.items)) return;
    if (seq < store._applied) return; // risposta vecchia: ignorata
    store._applied = seq;
    const prevTier = store.cart ? store.tier : null;
    const prevGift = store.gift;
    const prevFs = store.fsReached;
    store.cart = cart;
    store.metric = giftMetric(cart);
    store.tier = tierFor(store.metric);
    store.fsReached = store.fsCents > 0 && cart.total_price >= store.fsCents;
    const seg = store.tier >= 0 ? store.tiers[store.tier] : null;
    const giftLines = cart.items.filter(isGiftLine);
    const valid = giftLines.find(
      (l) =>
        seg &&
        (!seg.segmentId || l.properties._rule_id === seg.segmentId) &&
        (!seg.gifts.length || seg.gifts.some((g) => g.variantId === l.variant_id))
    );
    store.gift = valid
      ? {
          key: valid.key,
          variantId: valid.variant_id,
          title: cleanGiftTitle(valid.product_title) + (valid.variant_title ? ` · ${valid.variant_title}` : ''),
        }
      : null;

    // Cambio di livello durante l'uso del carrello (mai al caricamento della pagina)
    if (prevTier !== null && prevTier !== store.tier && !opts.silent) {
      const hadGift = !!prevGift || giftLines.length > 0;
      if (store.tier > prevTier && prevTier >= 0) {
        store.notice = 'up';
        store.noticeAgain = hadGift;
        store.listOpen = false;
      } else if (store.tier > prevTier) {
        store.notice = null; // primo prodotto nel carrello: nessun messaggio
      } else {
        const giftTier = parseInt(ss.get(KEY_GIFT_TIER), 10);
        // discesa: messaggio solo se l'omaggio scelto non e' piu' valido (downgrade vero)
        store.notice = hadGift && !store.gift && Number.isFinite(giftTier) && store.tier < giftTier && store.tier >= 0 ? 'down' : null;
        store.noticeAgain = true;
        store.listOpen = false;
      }
      if (store.tier > prevTier) {
        store.lastCelebration = { from: prevTier, to: store.tier, at: Date.now() };
        emit('tier-up', store.lastCelebration);
      } else {
        emit('tier-down', { from: prevTier, to: store.tier });
      }
    }
    // spedizione gratuita raggiunta senza cambiare livello: festa dedicata
    // (se cambia anche il livello, la spedizione e' nel messaggio di quel livello)
    if (prevTier !== null && !opts.silent && !prevFs && store.fsReached && !(store.tier > prevTier)) emit('fs-up');
    if (store.gift) {
      if (store.notice !== 'change') store.notice = null;
      if (!prevGift || prevGift.key !== store.gift.key) ss.set(KEY_GIFT_TIER, String(store.tier));
    }
    emit('change');

    // Omaggi non piu' validi (livello cambiato, o piu' di uno): li togliamo subito noi,
    // senza aspettare l'app (che ci mette ~2 s e poi ridisegna il carrello).
    const invalid = giftLines.filter((l) => l !== valid);
    if (invalid.length && !store.busy) removeLines(invalid.map((l) => l.key));
  };

  const removeLines = async (keys) => {
    store.busy = true;
    emit('change');
    try {
      const sections = drawerSectionIds();
      const updates = {};
      keys.forEach((k) => (updates[k] = 0));
      const res = await postJSON('cart/update.js', Object.assign({ updates }, sections ? { sections, sections_url: location.pathname } : {}));
      if (res.sections) renderDrawer(res.sections);
      store.busy = false;
      // la risposta della nostra modifica e' lo stato piu' recente: numero d'ordine assegnato all'arrivo
      applyCart(res, ++store._seq, { silent: true });
    } catch (e) {
      store.busy = false;
      refreshCart();
    }
  };

  // Scelta / cambio dell'omaggio: risultato mostrato subito, poi richieste a Shopify
  const chooseGift = async (variantId) => {
    if (store.busy || store.tier < 0) return;
    const seg = store.tiers[store.tier];
    if (!seg || !seg.segmentId) return;
    if (store.gift && store.gift.variantId === variantId) {
      store.listOpen = false;
      emit('change');
      return;
    }
    store.busy = true;
    store.pendingVariant = variantId;
    store.listOpen = false;
    store.error = null;
    emit('change');
    try {
      const sections = drawerSectionIds();
      const oldKeys = store.cart.items.filter(isGiftLine).map((l) => l.key);
      if (oldKeys.length) {
        const updates = {};
        oldKeys.forEach((k) => (updates[k] = 0));
        await postJSON('cart/update.js', { updates });
      }
      const res = await postJSON(
        'cart/add.js',
        Object.assign(
          {
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { _free_product: 'true', _rule_id: seg.segmentId, _kite_promo_name: CAMPAIGN_TITLE },
              },
            ],
          },
          sections ? { sections, sections_url: location.pathname } : {}
        )
      );
      if (res.sections) renderDrawer(res.sections);
      const cart = await getCart();
      store.busy = false;
      store.pendingVariant = null;
      store.notice = null;
      applyCart(cart, ++store._seq, { silent: true });
    } catch (e) {
      store.busy = false;
      store.pendingVariant = null;
      store.error = (e && (e.description || e.message)) || "Non è stato possibile aggiungere l'omaggio, riprova";
      emit('change');
      refreshCart();
    }
  };

  let refreshTimer = null;
  const refreshCart = () => {
    clearTimeout(refreshTimer);
    const seq = ++store._seq;
    refreshTimer = setTimeout(() => getCart().then((c) => applyCart(c, seq)).catch(() => {}), 40);
  };

  // Intercetta le modifiche al carrello fatte da tema e app: change/update/clear rispondono col
  // carrello intero (usato subito); add risponde solo con le righe aggiunte (si rilegge il carrello).
  // Risposta di /cart/add: contiene solo le righe aggiunte. Si costruisce subito il carrello "previsto"
  // (cosi' livello, messaggi e rimozione dell'omaggio non valido partono senza aspettare), poi si
  // rilegge il carrello vero come conferma.
  const provisionalCart = (added) => {
    if (!store.cart) return null;
    const cart = Object.assign({}, store.cart, { items: store.cart.items.slice() });
    added.forEach((item) => {
      if (!item || !item.key) return;
      const i = cart.items.findIndex((x) => x.key === item.key);
      const old = i >= 0 ? cart.items[i] : null;
      cart.total_price += (item.final_line_price || 0) - (old ? old.final_line_price : 0);
      cart.item_count += (item.quantity || 0) - (old ? old.quantity : 0);
      if (i >= 0) cart.items[i] = item;
      else cart.items.unshift(item);
    });
    return cart;
  };
  const onCartResponse = (url, res, seq) => {
    res
      .json()
      .then((data) => {
        if (data && Array.isArray(data.items) && 'total_price' in data && 'item_count' in data) return applyCart(data, seq);
        const added = data && Array.isArray(data.items) ? data.items : data && data.key ? [data] : null;
        const provisional = added && /\/cart\/add/.test(url) ? provisionalCart(added) : null;
        if (provisional) applyCart(provisional, seq);
        refreshCart();
      })
      .catch(() => refreshCart());
  };
  // Modifica di quantita' dal carrello (POST /cart/change con {line|id, quantity}): se il nuovo totale
  // rende l'omaggio non piu' valido, la richiesta diventa un'unica /cart/update.js che cambia la quantita'
  // e toglie l'omaggio insieme. Stessa risposta (carrello + sezioni) per il tema; l'app non deve correggere nulla.
  const mergeGiftRemoval = (url, init) => {
    if (!/\/cart\/change(\.js)?(\?|$)/.test(url) || !init || typeof init.body !== 'string' || !store.cart || !store.tiers.length) return null;
    let body;
    try {
      body = JSON.parse(init.body);
    } catch (e) {
      return null;
    }
    const items = store.cart.items;
    let idx = -1;
    if (body.line) idx = Number(body.line) - 1;
    else if (body.id) idx = items.findIndex((i) => i.key === String(body.id) || String(i.variant_id) === String(body.id));
    const line = items[idx];
    const qty = Number(body.quantity);
    if (!line || isGiftLine(line) || !Number.isFinite(qty) || qty < 0) return null;
    const unit = line.quantity ? lineValue(line) / line.quantity : 0;
    const newTier = tierFor(store.metric - lineValue(line) + unit * qty);
    const seg = newTier >= 0 ? store.tiers[newTier] : null;
    const remove = items.filter(
      (g) =>
        isGiftLine(g) &&
        !(seg && (!seg.segmentId || g.properties._rule_id === seg.segmentId) && (!seg.gifts.length || seg.gifts.some((x) => x.variantId === g.variant_id)))
    );
    if (!remove.length) return null;
    const updates = { [line.key]: qty };
    remove.forEach((g) => (updates[g.key] = 0));
    const merged = { updates };
    if (body.sections) merged.sections = body.sections;
    if (body.sections_url) merged.sections_url = body.sections_url;
    return [url.replace(/\/cart\/change(\.js)?/, '/cart/update.js'), Object.assign({}, init, { body: JSON.stringify(merged) })];
  };

  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    let url = typeof input === 'string' ? input : (input && input.url) || '';
    let args = arguments;
    if (typeof input === 'string') {
      const merged = mergeGiftRemoval(url, init);
      if (merged) {
        args = merged;
        url = merged[0];
      }
    }
    const req = origFetch.apply(this, args);
    if (CART_MUTATION_URL.test(url)) {
      const seq = ++store._seq;
      req.then((res) => onCartResponse(url, res.clone(), seq)).catch(() => {});
    }
    return req;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION_URL.test(String(url))) this.addEventListener('loadend', refreshCart);
    return origOpen.apply(this, arguments);
  };

  // Avvio: config dallo snippet, carrello iniziale incorporato nella pagina, config dell'app quando pronta
  const init = (cfg, initialCart) => {
    if (store.ready) return;
    store.ready = true;
    store.cfg = cfg;
    const cur = window.Shopify && window.Shopify.currency;
    store.rate = (cur && parseFloat(cur.rate)) || 1;
    buildTiers();
    if (initialCart) applyCart(initialCart, ++store._seq, { silent: true });
    else refreshCart();
    // la config dell'app omaggi arriva con il suo script: si aggiornano soglie e omaggi appena c'e'
    let tries = 0;
    const waitApp = setInterval(() => {
      tries += 1;
      if (appCampaign() || tries > 40) {
        clearInterval(waitApp);
        buildTiers();
        if (store.cart) applyCart(store.cart, ++store._seq, { silent: true });
      }
    }, 250);
  };

  window.SensCart = { store, subscribe, init, chooseGift, refreshCart, isGiftLine };

  // ---------------------------------------------------------------------------
  // Lampeggio verde e promemoria "Scegli il tuo omaggio"
  // ---------------------------------------------------------------------------
  const blinkEl = (el) => {
    if (!el || reduceMotion) return;
    el.classList.remove('sens-blink');
    el.getBoundingClientRect();
    el.classList.add('sens-blink');
    clearTimeout(el._sensBlink);
    el._sensBlink = setTimeout(() => el.classList.remove('sens-blink'), 2000);
  };
  const blinkPicker = () => {
    const p = document.querySelector('sens-gift-picker');
    if (p && p.blinkTarget) blinkEl(p.blinkTarget());
  };
  let remindTimer = null;
  let drawerOpen = false;
  const onDrawerToggle = () => {
    const open = document.documentElement.classList.contains(DRAWER_OPEN_CLASS);
    if (open === drawerOpen) return;
    drawerOpen = open;
    clearInterval(remindTimer);
    emit('drawer', { open });
    if (!open) return;
    setTimeout(blinkPicker, 600);
    remindTimer = setInterval(() => {
      if (!store.gift && store.tier >= 0 && !store.busy) blinkPicker();
    }, REMIND_MS);
  };
  new MutationObserver(onDrawerToggle).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  subscribe((type) => {
    if (type === 'tier-up' || type === 'tier-down') setTimeout(blinkPicker, 300);
  });

  // ---------------------------------------------------------------------------
  // <sens-gift-picker>: scelta dell'omaggio
  // ---------------------------------------------------------------------------
  const CHEVRON =
    '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class SensGiftPicker extends HTMLElement {
    connectedCallback() {
      if (this._unsub) return;
      this.innerHTML =
        '<p class="sens-gift__notice" hidden></p>' +
        '<div class="sens-gift__box">' +
        '<button type="button" class="sens-gift__head" aria-expanded="false"></button>' +
        '<div class="sens-gift__list" role="list" hidden></div>' +
        '</div>' +
        '<p class="sens-gift__error" role="alert" hidden></p>';
      this.el = {
        notice: this.querySelector('.sens-gift__notice'),
        box: this.querySelector('.sens-gift__box'),
        head: this.querySelector('.sens-gift__head'),
        list: this.querySelector('.sens-gift__list'),
        error: this.querySelector('.sens-gift__error'),
      };
      this.el.head.addEventListener('click', () => {
        if (store.busy) return;
        store.listOpen = !store.listOpen;
        if (store.listOpen && store.gift) store.notice = null;
        emit('change');
      });
      this.el.list.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-variant]');
        if (btn && !btn.disabled) chooseGift(Number(btn.dataset.variant));
      });
      this._unsub = subscribe((type) => {
        if (type === 'change') this.render();
      });
      this.render();
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      this._unsub = null;
    }

    // il riquadro intero (l'intestazione da sola verrebbe ritagliata dal riquadro)
    blinkTarget() {
      return this.el && !this.hidden && this.el.box;
    }

    render() {
      const s = store;
      const seg = s.tier >= 0 ? s.tiers[s.tier] : null;
      const show = !!(seg && seg.segmentId && seg.gifts.length);
      this.hidden = !show;
      if (!show) return;
      const chosenVariant = s.pendingVariant || (s.gift && s.gift.variantId);
      const chosen = chosenVariant ? seg.gifts.find((g) => g.variantId === chosenVariant) : null;
      const chosenTitle = chosen ? chosen.title : s.gift ? s.gift.title : '';

      // messaggio sopra il riquadro
      let notice = '';
      if (!chosenVariant) {
        if (s.notice === 'up') notice = s.noticeAgain ? '🎉 Hai sbloccato omaggi più ricchi! Sceglilo di nuovo' : '🎉 Hai sbloccato omaggi più ricchi! Scegline uno';
        else if (s.notice === 'down') notice = 'Il totale è cambiato: seleziona nuovamente il tuo omaggio';
      }
      this.el.notice.hidden = !notice;
      if (this.el.notice.textContent !== notice) this.el.notice.textContent = notice;

      // intestazione: "Scegli il tuo omaggio" oppure "✓ Il tuo omaggio: X · Cambia"
      const open = s.listOpen && !s.busy;
      let headKey;
      let headHtml;
      if (chosenVariant) {
        headKey = `c|${chosenTitle}|${s.busy}|${open}`;
        headHtml =
          `<span class="sens-gift__check" aria-hidden="true">${s.busy ? '<span class="sens-gift__spin"></span>' : '✓'}</span>` +
          `<span class="sens-gift__label">${s.busy ? 'Aggiungo il tuo omaggio…' : 'Il tuo omaggio:'} <b>${esc(chosenTitle)}</b></span>` +
          `<span class="sens-gift__action">${open ? 'Chiudi' : 'Cambia ›'}</span>`;
      } else {
        headKey = `n|${seg.gifts.length}|${open}|${s.busy}`;
        headHtml =
          '<span class="sens-gift__icon" aria-hidden="true">🎁</span>' +
          `<span class="sens-gift__label">${s.busy ? 'Aggiorno gli omaggi…' : 'Scegli il tuo omaggio'}</span>` +
          '<span class="sens-gift__badge" aria-hidden="true">1</span>' +
          `<span class="sens-gift__chev${open ? ' is-open' : ''}">${CHEVRON}</span>`;
      }
      if (this._headKey !== headKey) {
        this._headKey = headKey;
        this.el.head.innerHTML = headHtml;
      }
      this.el.box.classList.toggle('is-chosen', !!chosenVariant);
      this.el.box.classList.toggle('is-busy', s.busy);
      this.el.head.setAttribute('aria-expanded', String(open));

      // lista omaggi del livello attuale
      const listKey = `${s.tier}|${chosenVariant || ''}|${s.busy}`;
      if (this._listKey !== listKey) {
        this._listKey = listKey;
        this.el.list.innerHTML = seg.gifts
          .map((g) => {
            const isChosen = g.variantId === chosenVariant;
            return (
              `<div class="sens-gift__row${isChosen ? ' is-chosen' : ''}" role="listitem">` +
              (g.image ? `<img class="sens-gift__img" src="${esc(g.image)}&width=96" alt="" loading="lazy" width="48" height="48">` : '<span class="sens-gift__img"></span>') +
              `<span class="sens-gift__name">${esc(g.title)}<small>Omaggio</small></span>` +
              `<button type="button" class="sens-gift__btn" data-variant="${g.variantId}"${isChosen || s.busy ? ' disabled' : ''}>${isChosen ? 'Scelto ✓' : chosenVariant ? 'Scegli' : 'Aggiungi'}</button>` +
              '</div>'
            );
          })
          .join('');
      }
      this.el.list.hidden = !open;
      this.el.box.classList.toggle('is-open', open);

      this.el.error.hidden = !s.error;
      if (s.error) this.el.error.textContent = s.error;
    }
  }
  customElements.define('sens-gift-picker', SensGiftPicker);

  // ---------------------------------------------------------------------------
  // <sens-cart-bar>: barra omaggi scorrevole
  // ---------------------------------------------------------------------------
  const TRUCK =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>';
  const ICON = (d) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const ICONS = {
    gift: ICON('<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M12 8v13M12 8C10 4 7 4 7 6.2S9.5 8 12 8zm0 0c2-4 5-4 5-1.8S14.5 8 12 8z"/>'),
    hash: ICON('<path d="M5 19c0-8 5-13 14-14-.6 8.6-5.7 14-14 14zM5 19l7.5-7.5"/>'),
    oil: ICON('<path d="M12 3.5s6 6.4 6 10.8a6 6 0 0 1-12 0C6 9.9 12 3.5 12 3.5z"/>'),
    ext: ICON('<path d="M9.5 3h5M10.5 3v6.2L5.3 18.4A1.8 1.8 0 0 0 6.9 21h10.2a1.8 1.8 0 0 0 1.6-2.6L13.5 9.2V3M7.5 15h9"/>'),
  };
  const CONFETTI = ['#35af91', '#7bd389', '#ffb703', '#fb8500', '#8ecae6', '#ff5d8f'];

  class SensCartBar extends HTMLElement {
    connectedCallback() {
      if (this._unsub) return;
      const cfgEl = this.querySelector('[data-sens-config]');
      const cartEl = this.querySelector('[data-sens-cart]');
      let cfg = null;
      let cart = null;
      try {
        cfg = JSON.parse(cfgEl.textContent);
        cart = cartEl ? JSON.parse(cartEl.textContent) : null;
      } catch (e) {
        return;
      }
      init(cfg, cart);
      this.el = {
        toast: this.querySelector('[data-sens-toast]'),
        msg: this.querySelector('[data-sens-msg]'),
        next: this.querySelector('[data-sens-next]'),
        scroller: this.querySelector('[data-sens-scroller]'),
        track: this.querySelector('[data-sens-track]'),
        current: this.querySelector('[data-sens-current]'),
        pop: this.querySelector('[data-sens-pop]'),
      };
      const cur = window.Shopify && window.Shopify.currency;
      const code = (cur && cur.active) || 'EUR';
      const locale = document.documentElement.lang || 'it';
      this.money = new Intl.NumberFormat(locale, { style: 'currency', currency: code });
      this.moneyShort = new Intl.NumberFormat(locale, { style: 'currency', currency: code, maximumFractionDigits: 0 });
      this._page = undefined;
      this._shownTier = null;
      this.build();
      this.setupInteractions();
      this._unsub = subscribe((type, detail) => {
        if (type === 'change') {
          // durante la festa la barra la muove celebrate(): qui solo i testi
          if (this._celebrating) this.renderText();
          else this.render(false);
        } else if (type === 'tier-up') this.celebrate(detail.from, detail.to);
        else if (type === 'tier-down') this.render(true);
        else if (type === 'fs-up') this.celebrateFs();
        else if (type === 'drawer' && detail.open) this.onDrawerOpen();
      });
      this.render(false);
      // barra ricreata subito dopo un passaggio di livello (es. primo prodotto aggiunto): festeggia comunque
      const lc = store.lastCelebration;
      if (lc && Date.now() - lc.at < 1500 && !lc.done) this.celebrate(lc.from, lc.to);
      // il carrello chiuso ha larghezza 0: si riposiziona appena la barra ha una dimensione reale
      this._ro = new ResizeObserver(() => {
        if (this.el.scroller.clientWidth) this.scrollToPage(false);
      });
      this._ro.observe(this.el.scroller);
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      this._unsub = null;
      if (this._ro) this._ro.disconnect();
      clearTimeout(this._resetTimer);
      clearTimeout(this._popTimer);
      clearTimeout(this._toastTimer);
      cancelAnimationFrame(this._popRaf);
    }

    // --- costruzione della barra (tutti i traguardi equidistanti) ---
    // traguardi: i livelli omaggio piu' la spedizione gratuita, che e' un traguardo a parte
    // (se la sua soglia coincide con un livello, diventa il camioncino su quel livello)
    builtKey() {
      return `${store.tiers.map((t) => t.cents).join(',')}|${store.fsCents}`;
    }

    build() {
      const stops = store.tiers.map((t, i) => ({ cents: t.cents, tier: i }));
      if (store.fsCents > 0 && !store.tiers.some((t) => t.cents === store.fsCents)) stops.push({ cents: store.fsCents, fs: true });
      stops.sort((a, b) => a.cents - b.cents);
      this.stops = stops;
      this.fsStop = stops.findIndex((s) => s.fs);
      const n = stops.length;
      const pins = stops
        .map((s, k) => {
          const pos = `data-s="${k}" style="--p:${(k / (n - 1)).toFixed(4)}"`;
          if (s.fs) {
            return `<span class="sens-bar__pin sens-bar__pin--fs" data-fs ${pos}><span class="sens-bar__head">${TRUCK}</span><span class="sens-bar__amt">${this.moneyShort.format(s.cents / 100)}</span></span>`;
          }
          const t = store.tiers[s.tier];
          const fs = store.fsCents > 0 && t.cents === store.fsCents ? `<span class="sens-bar__fs">${TRUCK}</span>` : '';
          const amount = t.min <= 1 ? 'subito' : this.moneyShort.format(t.cents / 100);
          return `<span class="sens-bar__pin" data-i="${s.tier}" ${pos}><span class="sens-bar__head">${esc(t.name)}</span><span class="sens-bar__amt">${amount}</span>${fs}</span>`;
        })
        .join('');
      this.el.track.style.setProperty('--steps', n - 1);
      this.el.track.innerHTML = `<div class="sens-bar__rail"><div class="sens-bar__fill"></div></div>${pins}`;
      this.el.fill = this.el.track.querySelector('.sens-bar__fill');
      this._built = this.builtKey();
    }

    fillPct(metric) {
      const t = this.stops;
      const n = t.length;
      if (metric <= 0) return 0;
      for (let i = 0; i < n - 1; i++) {
        if (metric < t[i + 1].cents) return ((i + clamp((metric - t[i].cents) / (t[i + 1].cents - t[i].cents || 1), 0, 1)) / (n - 1)) * 100;
      }
      return 100;
    }

    // traguardo della barra di un livello omaggio (-1 se nessun livello)
    stopOf(tier) {
      return tier < 0 ? -1 : this.stops.findIndex((s) => s.tier === tier);
    }

    // blocco di due traguardi: 0→1→2, 2→3→4, ...
    pageFor(stop) {
      const maxStart = Math.max(this.stops.length - 3, 0);
      return Math.min(Math.floor(Math.max(stop, 0) / 2) * 2, maxStart);
    }

    render(animatePage, viewTier = store.tier) {
      if (!this.el || !store.cart) return;
      if (this._built !== this.builtKey()) {
        this.build();
        this._page = undefined;
      }
      this.hidden = store.metric <= 0 && !store.cart.item_count;
      this.el.fill.style.width = `${this.fillPct(store.metric)}%`;
      // spedizione: raggiunta col totale del carrello (durante la festa di un livello, solo se sta prima di quel livello)
      const viewCents = viewTier >= 0 ? store.tiers[viewTier].cents : 0;
      const fsDone = this.fsStop >= 0 && store.fsReached && (viewTier === store.tier || store.fsCents <= viewCents);
      // traguardo piu' avanti gia' raggiunto: guida il blocco mostrato e il prossimo traguardo
      const lead = Math.max(this.stopOf(viewTier), fsDone ? this.fsStop : -1);
      this.el.track.querySelectorAll('.sens-bar__pin').forEach((pin) => {
        const k = parseInt(pin.dataset.s, 10);
        pin.classList.toggle('is-next', k === lead + 1);
        if (pin.hasAttribute('data-fs')) {
          pin.classList.toggle('is-done', fsDone);
          return;
        }
        const i = parseInt(pin.dataset.i, 10);
        pin.classList.toggle('is-done', store.metric > 0 && i <= viewTier);
        pin.classList.toggle('is-current', store.metric > 0 && i === viewTier);
      });
      const page = this.pageFor(lead);
      if (page !== this._page) {
        const had = this._page !== undefined;
        this._page = page;
        this.hidePop();
        this.scrollToPage(animatePage || had);
      }
      this.renderText();
    }

    gifts(t, tag = 'b') {
      return ['hash', 'oil', 'ext']
        .filter((k) => t[k])
        .map((k) => `${esc(store.cfg.labels[k] || k)} <${tag}>${esc(t[k])}</${tag}>`)
        .join(' · ');
    }

    renderText() {
      const s = store;
      const t = s.tiers[s.tier];
      const next = s.tiers[s.tier + 1];
      const total = s.cart.total_price;
      const fsReached = s.fsCents > 0 && total >= s.fsCents;
      let msg;
      let nextLine = '';
      let current = '';
      if (s.tier < 0) {
        msg = "Aggiungi un prodotto: c'è un <b>omaggio</b> per te 🎁";
      } else if (!next) {
        msg = `🏆 Omaggio massimo <b>${esc(t.name)}</b> sbloccato!`;
      } else if (s.fsCents > 0 && !fsReached && s.fsCents < next.cents) {
        // il prossimo traguardo e' la spedizione gratuita
        const missingFs = this.money.format(Math.max(s.fsCents - total, 0) / 100);
        msg = `${isMobile() ? 'Mancano' : 'Ti mancano'} <b>${missingFs}</b> per la <b>spedizione gratuita</b> 🚚`;
        if (!isMobile()) nextLine = `Omaggio ${esc(next.name)} da ${this.moneyShort.format(next.cents / 100)}: ${this.gifts(next)}`;
      } else {
        const missing = this.money.format(Math.max(next.cents - s.metric, 0) / 100);
        const withFs = s.fsCents > 0 && !fsReached && next.cents >= s.fsCents;
        if (isMobile()) {
          msg = `Mancano <b>${missing}</b> per l'omaggio <b>${esc(next.name)}</b>${withFs ? ' + 🚚 gratis' : ''}`;
        } else {
          msg = `Ti mancano <b>${missing}</b> per l'omaggio <b>${esc(next.name)}</b>${withFs ? ' e la <b>spedizione gratuita</b>' : ''}`;
          nextLine = `Con ${esc(next.name)}: ${this.gifts(next)}`;
          if (!fsReached && !withFs && s.fsCents > 0) nextLine += `<br>🚚 Spedizione gratuita da ${this.moneyShort.format(s.fsCents / 100)}`;
        }
      }
      if (t && !isMobile()) {
        const chips = ['hash', 'oil', 'ext']
          .filter((k) => t[k])
          .map(
            (k) =>
              `<span class="sens-cur__chip">${ICONS[k]}<span>${esc(store.cfg.labels[k] || k)}</span><b>${esc(t[k])}</b></span>`
          )
          .join('');
        current =
          `<span class="sens-cur__icon">${ICONS.gift}</span>` +
          `<span class="sens-cur__body"><span class="sens-cur__top"><span class="sens-cur__title">Omaggio <b class="sens-cur__tier">${esc(
            t.name
          )}</b> sbloccato</span><span class="sens-cur__hint">scegline 1</span>${
            fsReached ? `<span class="sens-cur__fs">${TRUCK}Spedizione gratis</span>` : ''
          }</span><span class="sens-cur__chips">${chips}</span></span>`;
      }
      if (this._msg !== msg) this.el.msg.innerHTML = this._msg = msg;
      if (this._next !== nextLine) this.el.next.innerHTML = this._next = nextLine;
      if (this._current !== current) this.el.current.innerHTML = this._current = current;
    }

    // --- scorrimento ---
    scrollToPage(smooth) {
      const pin = this.el.track.querySelector(`.sens-bar__pin[data-s="${this._page}"]`);
      const sc = this.el.scroller;
      if (!pin || !sc.clientWidth) return;
      this._userScroll = false;
      const left = Math.max(0, pin.offsetLeft - EDGE);
      this._programmatic = true;
      clearTimeout(this._progTimer);
      this._progTimer = setTimeout(() => (this._programmatic = false), smooth && !reduceMotion ? 900 : 100);
      sc.scrollTo({ left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    }

    onDrawerOpen() {
      if (!this.isConnected) return;
      requestAnimationFrame(() => {
        this.scrollToPage(false);
        if (reduceMotion) return;
        setTimeout(() => {
          const sc = this.el.scroller;
          sc.classList.remove('is-nudge');
          sc.getBoundingClientRect();
          sc.classList.add('is-nudge');
          setTimeout(() => sc.classList.remove('is-nudge'), 1400);
        }, 350);
      });
    }

    scheduleReset() {
      clearTimeout(this._resetTimer);
      this._resetTimer = setTimeout(() => this.scrollToPage(true), RESET_MS);
    }

    setupInteractions() {
      const sc = this.el.scroller;
      const wrap = sc.parentElement;
      const arrows = wrap.querySelectorAll('[data-sens-arrow]');
      const updateEdges = () => {
        const max = sc.scrollWidth - sc.clientWidth;
        const atStart = sc.scrollLeft <= 2;
        const atEnd = sc.scrollLeft >= max - 2;
        sc.classList.toggle('is-start', atStart);
        sc.classList.toggle('is-end', atEnd);
        if (arrows[0]) arrows[0].disabled = atStart;
        if (arrows[1]) arrows[1].disabled = atEnd;
      };
      sc.addEventListener(
        'scroll',
        () => {
          updateEdges();
          // solo lo scorrimento fatto dal cliente chiude il fumetto (non le code dei nostri scorrimenti animati)
          if (this._programmatic || !this._userScroll) return;
          this.hidePop();
          this.scheduleReset();
        },
        { passive: true }
      );
      new ResizeObserver(updateEdges).observe(sc);
      const markUser = () => (this._userScroll = true);
      ['pointerdown', 'touchstart', 'wheel'].forEach((t) => sc.addEventListener(t, markUser, { passive: true }));
      arrows.forEach((btn) =>
        btn.addEventListener('click', () => {
          markUser();
          const pins = this.el.track.querySelectorAll('.sens-bar__pin');
          const step = pins.length > 1 ? pins[1].offsetLeft - pins[0].offsetLeft : 120;
          sc.scrollBy({ left: Number(btn.dataset.sensArrow) * step * 2, behavior: reduceMotion ? 'auto' : 'smooth' });
        })
      );

      // trascinamento col mouse (desktop) e tocchi
      let drag = null;
      sc.addEventListener('pointerdown', (e) => {
        drag = { id: e.pointerId, x: e.clientX, y: e.clientY, left: sc.scrollLeft, moved: false, mouse: e.pointerType === 'mouse' };
      });
      sc.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id || !drag.mouse) return;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) > 4) {
          drag.moved = true;
          sc.setPointerCapture(drag.id);
          sc.classList.add('is-dragging');
        }
        if (drag.moved) sc.scrollLeft = drag.left - dx;
      });
      const end = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const d = drag;
        drag = null;
        sc.classList.remove('is-dragging');
        const tap = !d.moved && Math.abs(e.clientX - d.x) < 6 && Math.abs(e.clientY - d.y) < 6;
        if (!tap) return;
        // tocco sul traguardo attuale o su uno non raggiunto: fumetto con cosa include;
        // altrove: si torna subito al livello raggiunto
        const pin = e.target.closest && e.target.closest('.sens-bar__pin');
        if (pin && (pin.hasAttribute('data-fs') || parseInt(pin.dataset.i, 10) >= store.tier)) {
          this.showPop(pin, {});
          return;
        }
        this.hidePop();
        clearTimeout(this._resetTimer);
        this.scrollToPage(true);
      };
      sc.addEventListener('pointerup', end);
      sc.addEventListener('pointercancel', () => {
        drag = null;
        sc.classList.remove('is-dragging');
      });

      this.el.pop.querySelector('[data-sens-pop-close]').addEventListener('click', () => {
        this.hidePop();
        this.scheduleReset();
      });
      this._onDocDown = (e) => {
        if (this.el.pop.hidden || this.el.pop.contains(e.target) || sc.contains(e.target)) return;
        this.hidePop();
        this.scheduleReset();
      };
      document.addEventListener('pointerdown', this._onDocDown, true);
    }

    // --- fumetto ---
    // fumetto del traguardo spedizione gratuita
    fsPopHtml(won) {
      const s = store;
      const next = s.tiers[s.tier + 1];
      const more = next ? `<p class="sens-pop__more">Con altri <b>${this.money.format(Math.max(next.cents - s.metric, 0) / 100)}</b> passi all'omaggio ${esc(next.name)}</p>` : '';
      if (won) return `<p class="sens-pop__title">🎉 <b>Spedizione gratuita</b> sbloccata!</p>${more}`;
      if (s.fsReached) return `<p class="sens-pop__title">✓ <b>Spedizione gratuita</b> sbloccata</p>${more}`;
      return (
        `<p class="sens-pop__title">🚚 Spedizione gratuita <span>da ${this.moneyShort.format(s.fsCents / 100)}</span></p>` +
        `<p class="sens-pop__missing">Ti mancano <b>${this.money.format(Math.max(s.fsCents - s.cart.total_price, 0) / 100)}</b></p>`
      );
    }

    showPop(pin, { won = false, crossedFs = false }) {
      const s = store;
      const FS = '<p class="sens-pop__fs">🚚 Spedizione gratuita</p>';
      let html;
      if (pin.hasAttribute('data-fs')) {
        html = this.fsPopHtml(won);
      } else {
        const i = parseInt(pin.dataset.i, 10);
        const t = s.tiers[i];
        const next = s.tiers[i + 1];
        const list = ['hash', 'oil', 'ext']
          .filter((k) => t[k])
          .map((k) => `<li>${esc(s.cfg.labels[k] || k)} <b>${esc(t[k])}</b></li>`)
          .join('');
        const more = next ? `<p class="sens-pop__more">Con altri <b>${this.money.format(Math.max(next.cents - s.metric, 0) / 100)}</b> passi all'omaggio ${esc(next.name)}</p>` : '';
        if (won) {
          html =
            `<p class="sens-pop__title">🎉 Omaggio <b>${esc(t.name)}</b> sbloccato!</p><p class="sens-pop__label">Scegline uno tra:</p><ul>${list}</ul>` +
            (crossedFs ? FS : '') +
            (more || '<p class="sens-pop__more">Hai raggiunto il livello massimo 🏆</p>');
        } else if (s.metric > 0 && s.metric >= t.cents) {
          html =
            `<p class="sens-pop__title">✓ Omaggio <b>${esc(t.name)}</b> sbloccato</p><p class="sens-pop__label">Scegline uno tra:</p><ul>${list}</ul>` +
            (s.fsReached ? FS : '') +
            more;
        } else {
          const withFs = s.fsCents > 0 && t.cents >= s.fsCents && !s.fsReached;
          html =
            `<p class="sens-pop__title">Omaggio <b>${esc(t.name)}</b> <span>da ${this.moneyShort.format(t.cents / 100)}</span></p>` +
            `<p class="sens-pop__missing">Ti mancano <b>${this.money.format(Math.max(t.cents - s.metric, 0) / 100)}</b></p>` +
            `<p class="sens-pop__label">Scegline uno tra:</p><ul>${list}</ul>` +
            (withFs ? FS : '');
        }
      }
      const pop = this.el.pop;
      pop.querySelector('[data-sens-pop-body]').innerHTML = html;
      pop.classList.toggle('is-win', won);
      this.el.track.querySelectorAll('.is-open').forEach((p) => p.classList.remove('is-open'));
      if (!won) pin.classList.add('is-open');
      pop.hidden = false;
      this._popPin = pin;
      this._popKey = null;
      this.positionPop();
      pop.classList.remove('is-in');
      pop.getBoundingClientRect();
      pop.classList.add('is-in');
      // la barra puo' muoversi mentre il fumetto e' aperto: il fumetto segue la sua card
      cancelAnimationFrame(this._popRaf);
      const follow = () => {
        if (pop.hidden) return;
        this.positionPop();
        this._popRaf = requestAnimationFrame(follow);
      };
      this._popRaf = requestAnimationFrame(follow);
      clearTimeout(this._resetTimer);
      clearTimeout(this._popTimer);
      this._popTimer = setTimeout(() => {
        this.hidePop();
        this.scrollToPage(true);
      }, won ? WIN_POP_MS : INFO_POP_MS);
    }

    positionPop() {
      const pin = this._popPin;
      const pop = this.el.pop;
      if (!pin || !pin.isConnected) return;
      const wr = pop.parentElement.getBoundingClientRect();
      const pr = pin.getBoundingClientRect();
      const cx = pr.left + pr.width / 2 - wr.left;
      const w = pop.offsetWidth;
      const left = clamp(cx - w / 2, 0, Math.max(wr.width - w, 0));
      const arrow = clamp(cx - left, 14, w - 14);
      const top = pr.bottom - wr.top + 10;
      const key = `${Math.round(left)}|${Math.round(top)}|${Math.round(arrow)}`;
      if (key === this._popKey) return;
      this._popKey = key;
      pop.style.left = `${left}px`;
      pop.style.top = `${top}px`;
      pop.style.setProperty('--ax', `${arrow}px`);
    }

    hidePop() {
      const pop = this.el && this.el.pop;
      if (!pop || pop.hidden) return;
      cancelAnimationFrame(this._popRaf);
      clearTimeout(this._popTimer);
      pop.hidden = true;
      pop.classList.remove('is-in');
      this.el.track.querySelectorAll('.is-open').forEach((p) => p.classList.remove('is-open'));
    }

    // --- festa al raggiungimento di un traguardo ---
    async celebrate(from, to) {
      if (!this.el) return;
      if (store.lastCelebration) store.lastCelebration.done = true;
      const seq = (this._celebSeq = (this._celebSeq || 0) + 1);
      this._celebrating = true;
      const done = () => {
        if (seq === this._celebSeq) this._celebrating = false;
      };
      // la barra si riempie fino al traguardo, poi il traguardo festeggia
      this.render(false, from);
      await wait(reduceMotion ? 0 : 750);
      if (seq !== this._celebSeq || !this.isConnected) return done();
      const pin = this.el.track.querySelector(`.sens-bar__pin[data-i="${to}"]`);
      if (!pin) {
        done();
        return this.render(true);
      }
      this.el.track.querySelectorAll('.sens-bar__pin').forEach((p) => p.classList.remove('is-current'));
      pin.classList.remove('is-next');
      pin.classList.add('is-done', 'is-current', 'is-reached');
      setTimeout(() => pin.classList.remove('is-reached'), 2200);
      this.confetti(pin);
      const fromCents = from < 0 ? 0 : store.tiers[from].cents;
      const crossedFs = store.fsCents > 0 && fromCents < store.fsCents && store.cart.total_price >= store.fsCents;
      // scorrimento al blocco successivo (se completato), poi il messaggio
      const page = this._page;
      done();
      this.render(true);
      if (this._page !== page && !reduceMotion) await wait(650);
      if (seq !== this._celebSeq || !this.isConnected) return;
      if (isMobile()) {
        this.showPop(pin, { won: true, crossedFs });
      } else {
        const t = store.tiers[to];
        const next = store.tiers[to + 1];
        this.el.toast.innerHTML =
          `<span class="sens-toast__title">🎉 Hai sbloccato l'omaggio ${esc(t.name)}!</span>Scegline uno tra: ${this.gifts(t)}` +
          (crossedFs ? '<br>🚚 Spedizione gratuita' : '') +
          (next
            ? `<br><small>Vuoi di più? Con altri <b>${this.money.format(Math.max(next.cents - store.metric, 0) / 100)}</b> passi all'omaggio ${esc(next.name)}.</small>`
            : '<br><small>Hai raggiunto il livello massimo 🏆</small>');
        this.el.toast.classList.add('is-show');
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => this.el.toast.classList.remove('is-show'), TOAST_MS);
      }
    }

    // --- festa al raggiungimento della spedizione gratuita (senza cambio di livello) ---
    async celebrateFs() {
      if (!this.el || this._celebrating) return;
      const page = this._page;
      this.render(true);
      const pin = this.el.track.querySelector('.sens-bar__pin[data-fs]');
      if (!pin) return;
      pin.classList.add('is-reached');
      setTimeout(() => pin.classList.remove('is-reached'), 2200);
      this.confetti(pin);
      if (this._page !== page && !reduceMotion) await wait(650);
      if (!this.isConnected) return;
      if (isMobile()) {
        this.showPop(pin, { won: true });
      } else {
        const next = store.tiers[store.tier + 1];
        this.el.toast.innerHTML =
          '<span class="sens-toast__title">🚚 Hai sbloccato la spedizione gratuita!</span>' +
          (next
            ? `<small>Con altri <b>${this.money.format(Math.max(next.cents - store.metric, 0) / 100)}</b> passi all'omaggio ${esc(next.name)}.</small>`
            : '');
        this.el.toast.classList.add('is-show');
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => this.el.toast.classList.remove('is-show'), TOAST_MS);
      }
    }

    confetti(pin) {
      if (reduceMotion) return;
      const layer = document.createElement('div');
      layer.className = 'sens-confetti';
      let html = '';
      for (let i = 0; i < 36; i++) {
        html += `<i style="--x:${pin.offsetLeft}px;--dx:${Math.round(Math.random() * 220 - 110)}px;--dy:${Math.round(-30 - Math.random() * 90)}px;--r:${Math.round(
          Math.random() * 720 - 360
        )}deg;--c:${CONFETTI[i % CONFETTI.length]};--d:${(Math.random() * 0.15).toFixed(2)}s"></i>`;
      }
      layer.innerHTML = html;
      this.el.track.appendChild(layer);
      setTimeout(() => layer.remove(), 1700);
    }
  }
  customElements.define('sens-cart-bar', SensCartBar);
})();
